import { useEffect, useState, useCallback } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, dt } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Wallet, Lock, CircleDollarSign, Plus, Loader2, Check, X, BadgeCheck, Banknote, FileDown,
} from "lucide-react";
import { toast } from "sonner";

const PStatus = ({ status }) => {
  const map = {
    pending: "bg-warning/15 text-warning border-warning/30",
    approved: "bg-chart-2/15 text-chart-2 border-chart-2/30",
    paid: "bg-success/15 text-success border-success/30",
    rejected: "bg-destructive/15 text-destructive border-destructive/30",
  };
  return <Badge variant="outline" className={`capitalize ${map[status]}`} data-testid={`payout-status-${status}`}>{status}</Badge>;
};

export default function Payouts() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [rows, setRows] = useState(null);
  const [balance, setBalance] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/payouts").then((r) => setRows(r.data)).catch(() => setRows([]));
    if (!isAdmin) api.get("/payouts/balance").then((r) => setBalance(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const request = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payouts", { amount: parseFloat(amount), note });
      toast.success("Withdrawal requested");
      setOpen(false); setAmount(""); setNote("");
      load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const process = async (id, action) => {
    try {
      await api.patch(`/payouts/${id}`, { action });
      toast.success({ approve: "Payout approved", reject: "Payout rejected", pay: "Marked as paid" }[action]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const receipt = (p) => {
    downloadFile(`/payouts/${p.id}/receipt`, {}, `woltpe_receipt_${p.id.slice(0, 8)}.pdf`)
      .catch(() => toast.error("Could not download receipt"));
  };

  const ReceiptBtn = ({ p }) => (
    <Button size="sm" variant="outline" onClick={() => receipt(p)} data-testid={`receipt-${p.id}`}>
      <FileDown className="mr-1.5 h-4 w-4" /> Receipt
    </Button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{isAdmin ? "Payout Requests" : "Payouts & Withdrawals"}</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Approve, reject and settle withdrawal requests from your network." : "Withdraw your available earnings."}
          </p>
        </div>
        {!isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-payout-button" disabled={!balance || balance.available <= 0}>
                <Plus className="mr-1.5 h-4 w-4" /> Request withdrawal
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="payout-dialog">
              <DialogHeader>
                <DialogTitle>Request a withdrawal</DialogTitle>
                <DialogDescription>Available balance: {balance ? inr(balance.available) : "—"}</DialogDescription>
              </DialogHeader>
              <form onSubmit={request} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Amount (₹)</Label>
                  <Input type="number" min="1" step="1" value={amount}
                    onChange={(e) => setAmount(e.target.value)} required data-testid="payout-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Settle to HDFC a/c" data-testid="payout-note-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="payout-submit-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isAdmin && balance && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Total Earned" value={inr(balance.earned)} icon={CircleDollarSign} accent="primary" testid="balance-earned" />
          <StatCard label="Locked / In-process" value={inr(balance.locked)} sub="Pending + approved" icon={Lock} accent="warning" testid="balance-locked" />
          <StatCard label="Paid Out" value={inr(balance.paid)} sub="Settled to you" icon={BadgeCheck} accent="success" testid="balance-paid" />
          <StatCard label="Available" value={inr(balance.available)} icon={Wallet} accent="secondary" testid="balance-available" />
        </div>
      )}

      <Card className="p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {isAdmin && <TableHead>Requested by</TableHead>}
              {isAdmin && <TableHead>Role</TableHead>}
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Settled</TableHead>
              {!isAdmin && <TableHead className="text-right">Receipt</TableHead>}
              {isAdmin && <TableHead className="text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {rows && rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No payout requests yet.</TableCell></TableRow>}
            {rows && rows.map((p) => (
              <TableRow key={p.id} data-testid={`payout-row-${p.id}`}>
                {isAdmin && <TableCell><div className="font-medium">{p.user_name}</div><div className="text-xs text-muted-foreground">{p.business_name}</div></TableCell>}
                {isAdmin && <TableCell><Badge variant="outline" className="capitalize">{p.role}</Badge></TableCell>}
                <TableCell className="text-right font-mono font-medium">{inr(p.amount)}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{p.note || "—"}</TableCell>
                <TableCell><PStatus status={p.status} /></TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(p.created_at)}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`payout-settled-${p.id}`}>{p.settled_at ? dt(p.settled_at) : "—"}</TableCell>
                {!isAdmin && (
                  <TableCell className="text-right">
                    {p.status === "paid" ? <ReceiptBtn p={p} /> : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                )}
                {isAdmin && (
                  <TableCell className="text-right">
                    {p.status === "pending" && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="icon" variant="outline" className="h-8 w-8 text-chart-2" onClick={() => process(p.id, "approve")} data-testid={`approve-${p.id}`}><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => process(p.id, "reject")} data-testid={`reject-${p.id}`}><X className="h-4 w-4" /></Button>
                      </div>
                    )}
                    {p.status === "approved" && (
                      <Button size="sm" variant="outline" className="text-success" onClick={() => process(p.id, "pay")} data-testid={`pay-${p.id}`}>
                        <Banknote className="mr-1.5 h-4 w-4" /> Mark paid
                      </Button>
                    )}
                    {p.status === "paid" && <ReceiptBtn p={p} />}
                    {p.status === "rejected" && <span className="text-xs text-muted-foreground">Done</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
